import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@mui/icons-material';

// Predefined common VLM prompts for different use cases
const PREDEFINED_PROMPTS = [
  {
    id: 'parking_violation',
    title: 'Parking Violation Detection',
    description: 'Detect vehicles parked in no-parking zones',
    prompt: 'Detect vehicles parked in no-parking zones or restricted areas. Alert when a vehicle remains stationary in these areas for more than 5 minutes.',
    category: 'Traffic Management',
    tags: ['parking', 'violation', 'traffic']
  },
  {
    id: 'crowd_density',
    title: 'Crowd Density Monitoring',
    description: 'Monitor crowd density and detect overcrowding',
    prompt: 'Monitor crowd density in public spaces. Alert when crowd density exceeds safe levels or when unusual crowd formations are detected.',
    category: 'Public Safety',
    tags: ['crowd', 'density', 'safety']
  },
  {
    id: 'vandalism_detection',
    title: 'Vandalism & Property Damage',
    description: 'Detect graffiti, vandalism, and property damage',
    prompt: 'Detect vandalism activities including graffiti, property damage, or suspicious behavior around public infrastructure. Alert immediately when such activities are observed.',
    category: 'Security',
    tags: ['vandalism', 'security', 'property']
  },
  {
    id: 'traffic_incident',
    title: 'Traffic Incident Detection',
    description: 'Detect accidents, breakdowns, and traffic incidents',
    prompt: 'Detect traffic incidents including vehicle accidents, breakdowns, debris on road, or unusual traffic patterns that may indicate incidents.',
    category: 'Traffic Management',
    tags: ['traffic', 'incident', 'accident']
  },
  {
    id: 'fire_smoke',
    title: 'Fire & Smoke Detection',
    description: 'Early detection of fire and smoke',
    prompt: 'Detect early signs of fire or smoke in the monitored area. Alert immediately upon detection of flames, smoke, or unusual heat signatures.',
    category: 'Emergency Response',
    tags: ['fire', 'smoke', 'emergency']
  },
  {
    id: 'unauthorized_access',
    title: 'Unauthorized Access',
    description: 'Detect unauthorized personnel in restricted areas',
    prompt: 'Monitor restricted areas and detect unauthorized personnel access. Alert when individuals enter secured zones without proper authorization.',
    category: 'Security',
    tags: ['access', 'security', 'restricted']
  },
  {
    id: 'weapon_detection',
    title: 'Weapon Detection',
    description: 'Detect weapons and dangerous objects',
    prompt: 'Detect visible weapons, knives, guns, or other dangerous objects carried by individuals. Immediate alert for any weapon detection.',
    category: 'Security',
    tags: ['weapon', 'security', 'danger']
  },
  {
    id: 'environmental_hazard',
    title: 'Environmental Hazards',
    description: 'Detect environmental hazards and spills',
    prompt: 'Detect environmental hazards including chemical spills, water leaks, structural damage, or other environmental threats in the monitored area.',
    category: 'Environmental',
    tags: ['environment', 'hazard', 'spill']
  }
];

const VLMPromptConfigDialog = ({ 
  open, 
  onClose, 
  onSave, 
  initialPrompts = [], 
  sensorName = 'Dynamic VLM Sensor' 
}) => {
  const [prompts, setPrompts] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState({
    prompt: '',
    title: '',
    category: 'Custom',
    isCustom: true
  });

  useEffect(() => {
    // Initialize prompts from props
    if (Array.isArray(initialPrompts)) {
      setPrompts([...initialPrompts]);
    } else if (initialPrompts && typeof initialPrompts === 'object') {
      // Handle single prompt object (backward compatibility)
      setPrompts([initialPrompts]);
    } else {
      setPrompts([]);
    }
  }, [initialPrompts]);

  const categories = ['All', ...new Set(PREDEFINED_PROMPTS.map(p => p.category))];

  const addPrompt = (promptConfig) => {
    setPrompts(prev => [...prev, { ...promptConfig, isCustom: promptConfig.isCustom || false }]);
    setEditingIndex(-1);
    setShowEditor(false);
    setEditingPrompt({ prompt: '', title: '', category: 'Custom', isCustom: true });
  };

  const addPredefinedPrompt = (predefinedPrompt) => {
    const promptConfig = {
      prompt: predefinedPrompt.prompt,
      title: predefinedPrompt.title,
      category: predefinedPrompt.category,
      tags: predefinedPrompt.tags,
      isCustom: false,
      predefinedId: predefinedPrompt.id
    };
    addPrompt(promptConfig);
  };

  const addCustomPrompt = () => {
    if (editingPrompt.prompt.trim() && editingPrompt.title.trim()) {
      if (editingIndex >= 0) {
        // Update existing prompt
        const updatedPrompts = [...prompts];
        updatedPrompts[editingIndex] = { ...editingPrompt };
        setPrompts(updatedPrompts);
      } else {
        // Add new prompt
        addPrompt(editingPrompt);
      }
    }
  };

  const removePrompt = (index) => {
    setPrompts(prev => prev.filter((_, i) => i !== index));
  };

  const editPrompt = (index) => {
    setEditingIndex(index);
    setShowEditor(true);
    setEditingPrompt({ ...prompts[index] });
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setShowEditor(false);
    setEditingPrompt({ prompt: '', title: '', category: 'Custom', isCustom: true });
  };

  const handleSave = () => {
    if (prompts.length === 0) {
      alert('Please add at least one prompt configuration.');
      return;
    }
    onSave(prompts);
    onClose();
  };

  const filteredPrompts = PREDEFINED_PROMPTS.filter(prompt => {
    const matchesSearch = prompt.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         prompt.description.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         prompt.tags.some(tag => tag.toLowerCase().includes(searchFilter.toLowerCase()));
    const matchesCategory = categoryFilter === 'All' || prompt.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleClose = () => {
    setPrompts(Array.isArray(initialPrompts) ? [...initialPrompts] : []);
    setShowEditor(false);
    cancelEdit();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { minHeight: '85vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Configure Multiple VLM Prompts for {sensorName}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          Configure multiple detection prompts for your Dynamic VLM sensor. Each prompt can detect different types of events.
        </Alert>

        <Grid container spacing={3}>
          {/* Current Prompts Section */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Configured Prompts ({prompts.length})
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={() => {
                  setEditingIndex(-1);
                  setShowEditor(true);
                  setEditingPrompt({ prompt: '', title: '', category: 'Custom', isCustom: true });
                }}
              >
                Add Custom
              </Button>
            </Box>

            <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 2 }}>
              {prompts.length === 0 ? (
                <Alert severity="warning">
                  No prompts configured yet. Add at least one prompt to continue.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {prompts.map((prompt, index) => (
                    <Grid item xs={12} key={index}>
                      <Card variant="outlined">
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {prompt.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {prompt.prompt.length > 100 ? `${prompt.prompt.substring(0, 100)}...` : prompt.prompt}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Chip label={prompt.category} size="small" color="primary" variant="outlined" />
                                {prompt.isCustom && <Chip label="Custom" size="small" variant="outlined" />}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton size="small" onClick={() => editPrompt(index)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => removePrompt(index)}
                                disabled={prompts.length === 1}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            {/* Custom Prompt Editor */}
            {showEditor && (
              <Card sx={{ border: 2, borderColor: 'primary.main' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {editingIndex >= 0 ? 'Edit Prompt' : 'Add Custom Prompt'}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Prompt Title"
                    value={editingPrompt.title}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, title: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Detection Prompt"
                    placeholder="Describe what you want this prompt to detect..."
                    value={editingPrompt.prompt}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, prompt: e.target.value }))}
                    helperText={`${editingPrompt.prompt.length}/1000 characters`}
                    inputProps={{ maxLength: 1000 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Category"
                    value={editingPrompt.category}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, category: e.target.value }))}
                    SelectProps={{ native: true }}
                    sx={{ mb: 2, minWidth: 150 }}
                  >
                    <option value="Custom">Custom</option>
                    {categories.filter(c => c !== 'All').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </TextField>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={cancelEdit}>Cancel</Button>
                  <Button 
                    size="small" 
                    variant="contained" 
                    onClick={addCustomPrompt}
                    disabled={!editingPrompt.prompt.trim() || !editingPrompt.title.trim()}
                  >
                    {editingIndex >= 0 ? 'Update' : 'Add'} Prompt
                  </Button>
                </CardActions>
              </Card>
            )}
          </Grid>

          {/* Predefined Prompts Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Predefined Prompts Library
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search prompts..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                select
                size="small"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 150 }}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </TextField>
            </Box>

            <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Grid container spacing={2}>
                {filteredPrompts.map((prompt) => (
                  <Grid item xs={12} key={prompt.id}>
                    <Card 
                      variant="outlined"
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 2 }
                      }}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {prompt.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          {prompt.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip label={prompt.category} size="small" color="primary" variant="outlined" />
                        </Box>
                      </CardContent>
                      <CardActions sx={{ pt: 0 }}>
                        <Button 
                          size="small" 
                          startIcon={<AddIcon />}
                          onClick={() => addPredefinedPrompt(prompt)}
                        >
                          Add
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> After saving, the sensor will retrain with all configured prompts.
            Training typically takes 2-7 days depending on the number and complexity of prompts.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={prompts.length === 0}
        >
          Save {prompts.length} Prompt{prompts.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VLMPromptConfigDialog;
